import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import UniDialog from '../../../Common/UniDialog';
import FullscreenLoading from '../../../Common/FullscreenLoading';
import { getUserPreference } from '../../../../utilities/common';
import { changePassword as axiosChangePassword } from '../../../../actions/userManagementActions';
import { showAlertSnackbar } from '../../../../reducers/uiSlice';

import {
    InputAdornment, IconButton, TextField, Button, List, ListItem, ListItemText
} from '@material-ui/core';
import { VisibilityOff, Visibility } from '@material-ui/icons';

function ChangePassword(props) {
    const {passwordChangeOpen, setPasswordChangeOpen} = props;

    const dispatch = useDispatch();
    const {t} = useTranslation('userManagement');
    let userPref = getUserPreference();

    const [oldPassword, setOldPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [newPasswordAgain, setNewPasswordAgain] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);

    function checkSubmitArgs() {
        let valid = true;

        if (!oldPassword || !newPassword || !newPasswordAgain) {
            valid = false;
        }

        return valid;
    }

    async function handleSubmit() {
        if (checkSubmitArgs()) {
            try {
                setSubmitLoading(true);
                setPasswordChangeOpen(false);

                await dispatch(axiosChangePassword(
                    userPref.username,
                    oldPassword,
                    newPassword,
                    newPasswordAgain
                ));
                
                dispatch(showAlertSnackbar({title: t('change_user_password'), 
                    message: t('password_change_success'),
                    type: 'success'
                }));
            } catch (error) {
                let msg;

                switch (error.message) {
                    case "Incorrect old password":
                        msg = t('incorrect_old_password');
                        break;
                    case "Password too weak":
                        msg = t('password_too_weak');
                        break;
                    case "Password confirmation does not match the password":
                        msg = t('retype_not_match');
                        break;
                    case "New password should be different from previous three passwords":
                        msg = t('repeat_passwords_detected');
                        break;
                    case "Authorization failed":
                        msg = t('auth_failed');
                        break;
                    case "Username incorrect":
                        msg = t('incorrect_username');
                        break;
                    default:
                        msg = t('password_change_failed');
                        break;
                }

                dispatch(showAlertSnackbar({title: t('change_user_password'), 
                    message: msg,
                    type: 'error'
                }));
            } finally {
                setSubmitLoading(false);
                handleClose();
            }
            
        }
    }

    return (<>

        <UniDialog title={t('change_user_password')}
            open={passwordChangeOpen}
            fullWidth
            maxWidth='xs'
            disableDefaultButtons
            onClose={handleClose}
        >
            <TextField label={t('current_password')}
                variant='outlined'
                fullWidth
                autoComplete='new-password'
                autoFocus={true}
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={ (e) => {setOldPassword(e.target.value)} }
                InputProps={{
                    endAdornment:
                        <InputAdornment position='end'>
                            <IconButton
                                tabIndex='-1'
                                onClick={ () => {setShowOldPassword(!showOldPassword)} }
                                onMouseDown={ (e) => {e.preventDefault()} }
                                onMouseUp={ (e) => {e.preventDefault()} }
                                edge='end'
                            >
                                {showOldPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        </InputAdornment>,
                }}
            />

            <div style={{marginTop: 10}}>
                {t('password_requirements')}

                <List dense={true}>
                    <ListItem style={{marginLeft: 10}}>
                        <ListItemText
                            primary={"• " + t('eight_or_more')}
                        />
                    </ListItem>

                    <ListItem style={{marginLeft: 10}}>
                        <ListItemText
                            secondaryTypographyProps={{component: 'span'}}
                            primary={"• " + t('one_of_each')}
                            secondary={
                                <div style={{marginLeft: 20}}>
                                    <div>{"○ " + t('uppercase_letter')}</div>
                                    <div>{"○ " + t('lowercase_letter')}</div>
                                    <div>{"○ " + t('number')}</div>
                                    <div>{"○ " + t('special_character')}</div>
                                </div>
                            }
                        />
                    </ListItem>

                    <ListItem style={{marginLeft: 10}}>
                        <ListItemText 
                            secondaryTypographyProps={{component: 'span'}}
                            primary={"• " + t('different_from')}
                            secondary={
                                <div style={{marginLeft: 20}}>
                                    {"○ " + t('three_recent_passwords')}
                                </div>
                            }
                        />
                    </ListItem>
                </List>
            </div>

            <TextField label={t('new_password')}
                variant='outlined'
                fullWidth
                autoComplete='new-password'
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={ (e) => {setNewPassword(e.target.value)} }
                InputProps={{
                    endAdornment:
                        <InputAdornment position='end'>
                            <IconButton
                                tabIndex='-1'
                                onClick={ () => {setShowNewPassword(!showNewPassword)} }
                                onMouseDown={ (e) => {e.preventDefault()} }
                                onMouseUp={ (e) => {e.preventDefault()} }
                                edge='end'
                            >
                                {showNewPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        </InputAdornment>,
                }}
            />

            <TextField label={t('retype_new_password')} style={{marginTop: 10}}
                variant='outlined'
                fullWidth
                autoComplete='new-password'
                type='password'
                value={newPasswordAgain}
                onChange={ (e) => {setNewPasswordAgain(e.target.value)} }
            />
            
            <div style={{marginTop: 10}}>
                <Button type='submit' onClick={handleClose} style={{marginRight:10}}>
                    {t('common:cancel')}
                </Button>
                
                <Button color='primary' onClick={handleSubmit}>
                    {t('common:submit')}
                </Button>  
            </div>
        </UniDialog>

        <FullscreenLoading open={submitLoading}/>
    </>);

    function handleClose() {
        setOldPassword('');
        setShowOldPassword(false);
        setNewPassword('');
        setShowNewPassword(false);
        setNewPasswordAgain('');

        setPasswordChangeOpen(false);
    }
}

export default ChangePassword;